import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';

import { AuthProvider, useAuth } from './contexts/AuthContext';

import Login from './components/auth/Login';
import Register from './components/auth/Register';
import Dashboard from './components/dashboard/Dashboard';
import Layout from './components/layout/Layout';

import Properties from './pages/Properties';
import Buildings from './pages/Buildings';
import Tenants from './pages/Tenants';
import Rent from './pages/Rent';
import Utilities from './pages/Utilities';
import Expenses from './pages/Expenses';
import Invoices from './pages/Invoices';
import Maintenance from './pages/Maintenance';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Integrations from './pages/Integrations';
import Owners from './pages/Owners';
import OwnerStatements from './pages/OwnerStatements';
import Leads from './pages/Leads';
import Vendors from './pages/Vendors';
import UnallocatedPayments from './pages/UnallocatedPayments';
import TenantDashboard from './pages/TenantDashboard';
import MyLease from './pages/tenant/MyLease';
import MyStatement from './pages/tenant/MyStatement';
import MyMaintenance from './pages/tenant/MyMaintenance';

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } } });

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
    const { user, loading } = useAuth();
    if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600" /></div>;
    if (!user) return <Navigate to="/login" replace />;
    if (allowedRoles.length > 0 && !allowedRoles.includes(user.user_level)) return <Navigate to="/dashboard" replace />;
    return children;
};

const LandlordRoute = ({ children }) => <ProtectedRoute allowedRoles={['admin','manager','landlord']}>{children}</ProtectedRoute>;
const OwnerOrManager = ({ children }) => <ProtectedRoute allowedRoles={['admin','manager','landlord','owner']}>{children}</ProtectedRoute>;
const TenantRoute   = ({ children }) => <ProtectedRoute allowedRoles={['tenant']}>{children}</ProtectedRoute>;

const RoleDashboard = () => {
    const { user } = useAuth();
    if (user?.user_level === 'tenant') return <TenantDashboard />;
    return <Dashboard />;
};

function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <AuthProvider>
                <Router>
                    <div className="App">
                        <Toaster position="top-right" toastOptions={{
                            duration: 4000,
                            style: { background: '#0f172a', color: '#fff', fontSize: 13, borderRadius: 8 },
                            success: { iconTheme: { primary: '#22c55e', secondary: '#fff' } },
                            error: { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
                        }} />
                        <Routes>
                            <Route path="/login" element={<Login />} />
                            <Route path="/register" element={<Register />} />

                            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                                <Route index element={<Navigate to="/dashboard" replace />} />
                                <Route path="dashboard" element={<RoleDashboard />} />

                                <Route path="properties" element={<LandlordRoute><Properties /></LandlordRoute>} />
                                <Route path="buildings"  element={<LandlordRoute><Buildings /></LandlordRoute>} />
                                <Route path="buildings/:id" element={<LandlordRoute><Buildings /></LandlordRoute>} />
                                <Route path="tenants"    element={<LandlordRoute><Tenants /></LandlordRoute>} />
                                <Route path="rent"       element={<LandlordRoute><Rent /></LandlordRoute>} />
                                <Route path="utilities"  element={<LandlordRoute><Utilities /></LandlordRoute>} />
                                <Route path="expenses"   element={<LandlordRoute><Expenses /></LandlordRoute>} />
                                <Route path="invoices"   element={<LandlordRoute><Invoices /></LandlordRoute>} />
                                <Route path="maintenance" element={<ProtectedRoute allowedRoles={['admin','manager','landlord','tenant']}><Maintenance /></ProtectedRoute>} />
                                <Route path="reports"    element={<LandlordRoute><Reports /></LandlordRoute>} />
                                <Route path="integrations" element={<LandlordRoute><Integrations /></LandlordRoute>} />

                                <Route path="owners" element={<LandlordRoute><Owners /></LandlordRoute>} />
                                <Route path="owner-statements" element={<OwnerOrManager><OwnerStatements /></OwnerOrManager>} />
                                <Route path="leads"   element={<LandlordRoute><Leads /></LandlordRoute>} />
                                <Route path="vendors" element={<LandlordRoute><Vendors /></LandlordRoute>} />
                                <Route path="unallocated-payments" element={<LandlordRoute><UnallocatedPayments /></LandlordRoute>} />

                                <Route path="portal/statement" element={<TenantRoute><MyStatement /></TenantRoute>} />
                                <Route path="portal/lease" element={<TenantRoute><MyLease /></TenantRoute>} />
                                <Route path="portal/maintenance" element={<TenantRoute><MyMaintenance /></TenantRoute>} />

                                <Route path="settings" element={<ProtectedRoute allowedRoles={['admin','manager','landlord','tenant','owner']}><Settings /></ProtectedRoute>} />
                            </Route>

                            <Route path="*" element={<Navigate to="/dashboard" replace />} />
                        </Routes>
                    </div>
                </Router>
            </AuthProvider>
        </QueryClientProvider>
    );
}

export default App;
